/**
 * Ayah Meaning Drawer
 * Shows Thai translation (meaning) for a selected ayah with a listen button.
 * Supports two translation sources:
 *   - qf: Quran Foundation API (translation ID 51)
 *   - enc: QuranEnc.com (thai_mokhtasar)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { BookText, Volume2, Loader2, Pause } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getAyahWithTranslations, getVerseAudio, getQuranEncTranslation } from '@/services/quran.service';
import type { Ayah } from '@/types/quran';
import { cn } from '@/lib/utils';

type TranslationSource = 'qf' | 'enc';

interface TafsirDrawerProps {
  open: boolean;
  onClose: () => void;
  surahNumber: number;
  ayahNumber: number;
  /** Thai translation ID for QF source — default 51 (King Fahad) */
  selectedTafsirId?: number;
  /** Reciter ID for audio playback */
  selectedReciterId?: string;
  /** Initial translation source */
  translationSource?: TranslationSource;
}

export function TafsirDrawer({
  open,
  onClose,
  surahNumber,
  ayahNumber,
  selectedTafsirId = 51,
  selectedReciterId = '7',
  translationSource: initialSource = 'qf',
}: TafsirDrawerProps) {
  const [source, setSource] = useState<TranslationSource>(initialSource);
  const [ayah, setAyah] = useState<Ayah | null>(null);
  const [encTranslation, setEncTranslation] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Audio state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  // Fetch translation based on selected source
  useEffect(() => {
    if (!open || surahNumber <= 0 || ayahNumber <= 0) return;

    setLoading(true);
    setAyah(null);
    setEncTranslation(null);

    if (source === 'qf') {
      getAyahWithTranslations(surahNumber, ayahNumber, [selectedTafsirId])
        .then(setAyah)
        .catch(() => setAyah(null))
        .finally(() => setLoading(false));
    } else {
      // QuranEnc: fetch surah, then find the specific ayah
      Promise.all([
        getQuranEncTranslation(surahNumber),
        // Also fetch ayah for Arabic text
        getAyahWithTranslations(surahNumber, ayahNumber, []).catch(() => null),
      ])
        .then(([encData, ayahData]) => {
          if (ayahData) setAyah(ayahData);
          const match = encData.find((a) => String(a.aya) === String(ayahNumber));
          setEncTranslation(match?.translation ?? null);
        })
        .catch(() => {
          setAyah(null);
          setEncTranslation(null);
        })
        .finally(() => setLoading(false));
    }
  }, [open, surahNumber, ayahNumber, selectedTafsirId, source]);

  // Stop audio when drawer closes or ayah changes
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsAudioLoading(false);
  }, []);

  useEffect(() => {
    if (!open) stopAudio();
  }, [open, stopAudio]);

  useEffect(() => {
    stopAudio();
  }, [surahNumber, ayahNumber, stopAudio]);

  const handleTogglePlay = useCallback(async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    if (audioRef.current && !isPlaying) {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
      return;
    }

    setIsAudioLoading(true);
    try {
      const url = await getVerseAudio(surahNumber, ayahNumber, selectedReciterId);
      if (!url) {
        toast.error('ไม่พบเสียงอายะฮ์นี้');
        return;
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
      };
      audio.onerror = () => {
        toast.error('เล่นเสียงไม่สำเร็จ');
        setIsPlaying(false);
        audioRef.current = null;
      };
      await audio.play();
      setIsPlaying(true);
    } catch {
      toast.error('เล่นเสียงไม่สำเร็จ');
      setIsPlaying(false);
    } finally {
      setIsAudioLoading(false);
    }
  }, [isPlaying, surahNumber, ayahNumber, selectedReciterId]);

  // Determine which translation text to show
  const translationText =
    source === 'qf'
      ? ayah?.translations?.[0]?.text
      : encTranslation;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookText className="w-5 h-5" />
            ความหมาย — {surahNumber}:{ayahNumber}
          </SheetTitle>
          <SheetDescription>
            แตะปุ่มฟังเพื่อฟังการอ่านอายะฮ์นี้
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Source selector */}
          <div className="flex gap-2">
            <button
              onClick={() => setSource('qf')}
              className={cn(
                'flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
                source === 'qf'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              Quran Foundation
            </button>
            <button
              onClick={() => setSource('enc')}
              className={cn(
                'flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
                source === 'enc'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              QuranEnc (มุคตะศ็อร)
            </button>
          </div>

          {/* Ayah Arabic text + Listen button */}
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ) : (
            <>
              {ayah && (
                <div className="relative p-4 rounded-xl bg-muted/50 border border-border/50">
                  <div
                    className="font-amiri-quran text-xl text-right leading-loose pr-12"
                    dir="rtl"
                  >
                    {ayah.text}
                  </div>

                  {/* Listen button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePlay();
                    }}
                    className={cn(
                      'absolute top-3 right-3 p-2.5 rounded-full transition-colors',
                      isPlaying
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    )}
                    aria-label="ฟังอายะฮ์"
                  >
                    {isAudioLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isPlaying ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}

              {/* Translation text */}
              {translationText ? (
                <div className="text-sm leading-relaxed text-foreground px-1">
                  <span dangerouslySetInnerHTML={{ __html: translationText }} />
                </div>
              ) : !loading ? (
                <p className="text-sm text-muted-foreground">ไม่พบคำแปลสำหรับอายะฮ์นี้</p>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default TafsirDrawer;
